import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';

const ErrorScreen = ({ error, resetError }) => {
  const { t } = useTranslation();

  const handleRefresh = () => {
    if (resetError) {
      resetError();
    } else {
      window.location.reload();
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        
        {/* Error Icon */}
        <div className="mb-8">
          <div className="w-24 h-24 mx-auto bg-red-100 rounded-full flex items-center justify-center">
            <AlertTriangle className="w-12 h-12 text-red-500" />
          </div>
        </div>

        {/* Error Message */}
        <div className="mb-8">
          <h1 className="text-3xl lg:text-4xl font-sofia font-bold text-primary mb-4">
            {t('errors.general.title')}
          </h1>
          <p className="text-lg text-text-secondary font-sofia leading-relaxed mb-4">
            {t('errors.general.message')}
          </p>
          
          {/* Error Details (Development Only) */}
          {process.env.NODE_ENV === 'development' && error && (
            <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg text-left">
              <h3 className="text-sm font-sofia font-semibold text-red-800 mb-2">
                Error Details (Development):
              </h3>
              <pre className="text-xs text-red-700 overflow-auto">
                {error.toString()}
              </pre>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-12">
          <button
            onClick={handleRefresh}
            className="inline-flex items-center px-6 py-3 bg-accent text-white font-sofia font-medium rounded-lg hover:bg-accent/90 transition-all duration-200 hover:scale-105"
          >
            <RefreshCw className="w-5 h-5 mr-2" />
            {t('errors.general.tryAgain')}
          </button>
          
          <Link
            to="/"
            className="inline-flex items-center px-6 py-3 border-2 border-primary text-primary font-sofia font-medium rounded-lg hover:bg-primary hover:text-white transition-all duration-200 hover:scale-105"
          >
            <Home className="w-5 h-5 mr-2" />
            {t('errors.general.backToHome')}
          </Link>
        </div>

        {/* Helpful Information */}
        <div className="bg-white p-6 rounded-lg shadow-soft">
          <h3 className="text-lg font-sofia font-semibold text-primary mb-4">
            {t('errors.general.whatToDo')}
          </h3>
          <ul className="text-left text-text-secondary font-sofia space-y-2">
            {t('errors.general.suggestions', { returnObjects: true }).map((suggestion, index) => (
              <li key={index} className="flex items-start">
                <span className="text-accent mr-2">•</span>
                {suggestion}
              </li>
            ))}
          </ul>
        </div>

        {/* Contact Support */}
        <div className="mt-8 p-6 bg-primary/5 rounded-lg">
          <h3 className="text-lg font-sofia font-semibold text-primary mb-4">
            {t('errors.general.stillNeedHelp')}
          </h3>
          <p className="text-text-secondary font-sofia mb-4">
            {t('errors.general.contactSupport')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <a
              href={`mailto:${t('contact.email')}?subject=${encodeURIComponent(t('errors.general.emailSubject'))}`}
              className="inline-flex items-center px-4 py-2 bg-accent text-white font-sofia font-medium rounded-lg hover:bg-accent/90 transition-colors duration-200"
            >
              📧 {t('errors.general.emailSupport')}
            </a>
            <a
              href={`tel:${t('contact.phone.morocco').replace(/\s/g, '')}`}
              className="inline-flex items-center px-4 py-2 border border-accent text-accent font-sofia font-medium rounded-lg hover:bg-accent hover:text-white transition-colors duration-200"
            >
              📞 {t('errors.general.callSupport')}
            </a>
          </div>
        </div>

      </div>
    </div>
  );
};

export default ErrorScreen;
